import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';

interface LocationInputProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

declare global {
  interface Window {
    google: any;
    initGoogleMapsCallback: () => void;
  }
}

// Create a global script loader to ensure we only load the script once
let scriptPromise: Promise<void> | null = null;

const loadGoogleMapsScript = (): Promise<void> => {
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise<void>((resolve) => {
    // If already loaded, resolve immediately
    if (window.google?.maps?.places) {
      resolve();
      return;
    }

    // Define the callback function
    window.initGoogleMapsCallback = () => {
      resolve();
    };

    // Create script element
    const script = document.createElement('script');
    // Use loading=async parameter as recommended by Google
    script.src = `https://maps.googleapis.com/maps/api/js?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async&callback=initGoogleMapsCallback`;
    script.async = true;
    script.defer = true;
    
    // Handle errors
    script.onerror = (error) => {
      console.error('Error loading Google Maps script:', error);
      scriptPromise = null;
    };

    // Append to document
    document.head.appendChild(script);
  });

  return scriptPromise;
};

function debounce<T extends (...args: any[]) => void>(fn: T, delay: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

const LocationInput: React.FC<LocationInputProps> = ({ value, onChange, className }) => {
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const sessionTokenRef = useRef<any>(null);

  useEffect(() => {
    const loadApi = async () => {
      try {
        if (window.google?.maps?.places) {
          setIsApiLoaded(true);
          return;
        }
        await loadGoogleMapsScript();
        setIsApiLoaded(true);
      } catch (error) {
        console.error('Failed to load Google Maps API:', error);
      }
    };
    loadApi();
  }, []);

  // Debounced fetch
  const fetchPredictions = useCallback(
    debounce(async (input: string) => {
      if (!isApiLoaded || !input) {
        setPredictions([]);
        return;
      }
      try {
        const { AutocompleteSessionToken, AutocompleteSuggestion } = await window.google.maps.importLibrary('places');
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new AutocompleteSessionToken();
        }
        const request = {
          input,
          sessionToken: sessionTokenRef.current,
        };
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
        setPredictions(suggestions || []);
        setShowDropdown(true);
        setHighlightedIndex(-1);
      } catch (error) {
        setPredictions([]);
        setShowDropdown(false);
      }
    }, 300),
    [isApiLoaded]
  );

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    fetchPredictions(e.target.value);
  };

  // Handle selection
  const handleSelect = async (suggestion: any) => {
    setShowDropdown(false);
    setPredictions([]);
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['formattedAddress'] });
      if (place.formattedAddress) {
        onChange(place.formattedAddress);
      } else {
        onChange(suggestion.placePrediction.text.toString());
      }
    } catch {
      onChange(suggestion.placePrediction.text.toString());
    }
    // Reset session token for next session
    sessionTokenRef.current = null;
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || predictions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % predictions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 + predictions.length) % predictions.length);
    } else if (e.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < predictions.length) {
        handleSelect(predictions[highlightedIndex]);
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Hide dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative group">
      <div className="absolute inset-0 bg-gradient-to-r from-gray-50/0 via-gray-100/50 to-gray-50/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg"></div>
      <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 stroke-[#468BFF] transition-all duration-200 group-hover:stroke-[#8FBCFA] z-10" strokeWidth={1.5} />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        className={className}
        placeholder="City, Country"
        autoComplete="off"
        onFocus={() => value && predictions.length > 0 && setShowDropdown(true)}
      />
      {showDropdown && predictions.length > 0 && (
        <ul
          ref={dropdownRef}
          className="absolute left-0 right-0 mt-2 z-20 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto"
        >
          {predictions.map((suggestion, idx) => (
            <li
              key={suggestion.placePrediction.id || idx}
              className={`px-4 py-2 cursor-pointer transition-colors ${
                idx === highlightedIndex ? 'bg-blue-50' : ''
              }`}
              onMouseDown={() => handleSelect(suggestion)}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              {suggestion.placePrediction.text.toString()}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LocationInput; 